import React, { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useUserContext } from '../../contexts';
import { meFn } from '../../lib/api';
import { getEnvironment } from '../../lib/utils';
import {
    clearExperiment,
    EXPERIMENT_REGISTRY,
    getExperimentCohort,
} from '../../lib/experimentRegistry';
import {
    BILLING_PERIOD_ASSIGNMENT_SETTING_KEY,
    BILLING_PERIOD_EXPERIMENT_ID,
    setBillingPeriodAssignmentId,
} from '../../lib/billingPeriodExperiment';
import './ExperimentDebugTool.scss';

const isDebugEnvironment = () => getEnvironment() !== 'production';

const formatPercent = (value) => `${value}%`;

const publishExperimentChange = (experiment, detail) => {
    window.dispatchEvent(new CustomEvent('crate:experiment-updated', {
        detail: {
            experimentId: experiment.id,
            ...detail,
        },
    }));
};

const getUserSettings = (user) => (
    user?.settings && typeof user.settings === 'object' ? user.settings : {}
);

const ExperimentRow = ({ experiment, assignment, onChange, onClear, isSaving }) => {
    const selectedCohort = assignment.cohort;

    return (
        <section className="experiment-debug-tool__experiment" aria-labelledby={`experiment-${experiment.id}`}>
            <div className="experiment-debug-tool__experiment-header">
                <div>
                    <h2 id={`experiment-${experiment.id}`}>{experiment.label}</h2>
                    <p>{experiment.id} · {assignment.source}</p>
                </div>
                <span className={`experiment-debug-tool__cohort ${selectedCohort ? 'is-selected' : ''}`}>
                    {selectedCohort || '—'}
                </span>
            </div>

            <div className="experiment-debug-tool__allocation" aria-label={`${experiment.label} allocation probabilities`}>
                {experiment.buckets.map((bucket) => {
                    const percentage = experiment.bucketWeights[bucket] ?? 0;
                    const isSelected = selectedCohort === bucket;

                    return (
                        <button
                            key={bucket}
                            type="button"
                            className={`experiment-debug-tool__bucket ${isSelected ? 'is-selected' : ''}`}
                            aria-pressed={isSelected}
                            disabled={isSaving}
                            onClick={() => onChange(experiment, bucket)}
                        >
                            <span className="experiment-debug-tool__bucket-label">{bucket}</span>
                            <span className="experiment-debug-tool__bucket-percent">{formatPercent(percentage)}</span>
                        </button>
                    );
                })}
            </div>

            <button
                type="button"
                className="experiment-debug-tool__clear"
                disabled={isSaving || !selectedCohort}
                onClick={() => onClear(experiment)}
            >
                {isSaving ? 'Saving…' : 'Clear assignment'}
            </button>
        </section>
    );
};

const ExperimentDebugTool = () => {
    const { user, setUser } = useUserContext();
    const [isOpen, setIsOpen] = useState(false);
    const [savingExperimentId, setSavingExperimentId] = useState(null);
    const [error, setError] = useState('');

    const assignments = EXPERIMENT_REGISTRY.map((experiment) => ({
        experiment,
        assignment: getExperimentCohort(experiment, user),
    }));

    const updateUserSettings = useCallback((changes) => {
        if (!user?.id) {
            return;
        }

        setUser((currentUser) => {
            if (!currentUser || currentUser.id !== user.id) {
                return currentUser;
            }

            const currentSettings = getUserSettings(currentUser);
            const nextSettings = { ...currentSettings };
            Object.entries(changes).forEach(([key, value]) => {
                if (value === null) {
                    delete nextSettings[key];
                } else {
                    nextSettings[key] = value;
                }
            });

            return { ...currentUser, settings: nextSettings };
        });
    }, [setUser, user?.id]);

    const saveAssignment = useCallback(async (experiment, cohort) => {
        setError('');
        setSavingExperimentId(experiment.id);

        try {
            let billingPeriodAssignmentId = null;
            if (user?.id) {
                const currentAssignment = getExperimentCohort(experiment, user).cohort;
                const shouldResetBeforeChange = Boolean(
                    experiment.resetBeforeChange
                    && currentAssignment
                    && currentAssignment !== cohort
                );
                const changes = {
                    [experiment.cohortSettingKey]: cohort,
                    [experiment.experimentSettingKey]: '1',
                };

                if (experiment.id === BILLING_PERIOD_EXPERIMENT_ID) {
                    changes[BILLING_PERIOD_ASSIGNMENT_SETTING_KEY] = uuidv4();
                    billingPeriodAssignmentId = changes[BILLING_PERIOD_ASSIGNMENT_SETTING_KEY];
                }

                if (shouldResetBeforeChange) {
                    const resetResponse = await meFn({
                        [experiment.cohortSettingKey]: null,
                        [experiment.experimentSettingKey]: null,
                    });
                    if (
                        !resetResponse
                        || (experiment.id === 'marketplace_v1' && resetResponse.marketplace_experiment !== null)
                    ) {
                        throw new Error('The existing account assignment could not be reset.');
                    }
                }

                const response = await meFn(changes);
                if (!response) {
                    throw new Error('The account settings could not be updated.');
                }
                updateUserSettings(changes);
            }
            experiment.setBrowserCohort(cohort);
            if (experiment.id === BILLING_PERIOD_EXPERIMENT_ID) {
                setBillingPeriodAssignmentId(billingPeriodAssignmentId ?? uuidv4());
            }
            publishExperimentChange(experiment, { cohort });
        } catch (saveError) {
            setError(saveError.message || 'The assignment could not be saved.');
        } finally {
            setSavingExperimentId(null);
        }
    }, [updateUserSettings, user]);

    const clearAssignment = useCallback(async (experiment) => {
        setError('');
        setSavingExperimentId(experiment.id);
        clearExperiment(experiment);

        try {
            if (user?.id) {
                const changes = {
                    [experiment.cohortSettingKey]: null,
                    [experiment.experimentSettingKey]: null,
                };
                if (experiment.id === BILLING_PERIOD_EXPERIMENT_ID) {
                    changes[BILLING_PERIOD_ASSIGNMENT_SETTING_KEY] = null;
                }
                const response = await meFn(changes);
                if (
                    !response
                    || (experiment.id === 'marketplace_v1' && response.marketplace_experiment !== null)
                ) {
                    throw new Error('The account settings could not be updated.');
                }
                updateUserSettings(changes);
            }
            publishExperimentChange(experiment, { cleared: true });
        } catch (clearError) {
            setError(clearError.message || 'The assignment could not be cleared.');
        } finally {
            setSavingExperimentId(null);
        }
    }, [updateUserSettings, user?.id]);

    if (!isDebugEnvironment()) {
        return null;
    }

    return (
        <aside className={`experiment-debug-tool ${isOpen ? 'is-open' : ''}`}>
            <button
                type="button"
                className="experiment-debug-tool__toggle"
                aria-expanded={isOpen}
                aria-controls="experiment-debug-tool-panel"
                onClick={() => setIsOpen((open) => !open)}
            >
                <span className="experiment-debug-tool__status-dot" aria-hidden="true" />
                <span>EXP</span>
                <span className="experiment-debug-tool__environment">{getEnvironment()}</span>
            </button>

            {isOpen && (
                <div id="experiment-debug-tool-panel" className="experiment-debug-tool__panel">
                    <div className="experiment-debug-tool__panel-header">
                        <div>
                            <p className="experiment-debug-tool__eyebrow">Testing utility</p>
                            <h1>Experiment console</h1>
                        </div>
                        <button
                            type="button"
                            className="experiment-debug-tool__close"
                            aria-label="Close experiment console"
                            onClick={() => setIsOpen(false)}
                        >
                            ×
                        </button>
                    </div>

                    <p className="experiment-debug-tool__hint">
                        Override assignments locally or for the signed-in account.
                    </p>

                    {assignments.map(({ experiment, assignment }) => (
                        <ExperimentRow
                            key={experiment.id}
                            experiment={experiment}
                            assignment={assignment}
                            isSaving={savingExperimentId === experiment.id}
                            onChange={saveAssignment}
                            onClear={clearAssignment}
                        />
                    ))}

                    {error && <p className="experiment-debug-tool__error" role="alert">{error}</p>}
                </div>
            )}
        </aside>
    );
};

export default ExperimentDebugTool;
